<template>
  <AModal
    :model-value="show"
    title="저작물 추가"
    size="md"
    @update:model-value="(v) => !v && closeModal()"
  >
    <!-- Step 1: 검색 -->
    <div v-if="!selectedTextbook">
      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">search</i>
          교재 검색
        </label>
        <div class="add-tb__search-row">
          <input
            v-model="searchQuery"
            type="text"
            class="p-input"
            placeholder="교재명 또는 바코드로 검색..."
            @input="debounceSearch"
            @keyup.enter="searchTextbooks"
          />
          <ABtn kind="yellow" :disabled="searchLoading" :loading="searchLoading" @click="searchTextbooks">
            검색
          </ABtn>
        </div>
      </div>

      <div v-if="searchLoading" class="add-tb__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>검색 중...</p>
      </div>

      <div v-else-if="searchResults.length > 0" class="add-tb__results">
        <button
          v-for="book in searchResults"
          :key="book.idx"
          type="button"
          class="add-tb__result"
          @click="selectTextbookForAdd(book)"
        >
          <img v-if="book.cover_image_url" :src="book.cover_image_url" :alt="book.title" class="add-tb__cover-sm" @error="handleImageError" />
          <div v-else class="add-tb__cover-sm add-tb__cover-fb">
            <i class="material-icons-round">menu_book</i>
          </div>
          <div>
            <p class="add-tb__title">{{ book.title }}</p>
            <p class="add-tb__meta">{{ book.barcode }} | {{ book.publisher }}</p>
          </div>
        </button>
      </div>

      <AEmpty
        v-else-if="searched && searchResults.length === 0"
        title="검색 결과가 없습니다"
        size="sm"
      />

      <AEmpty
        v-else
        title="교재를 검색해주세요"
        sub="교재명 또는 바코드를 입력하여 검색하세요"
        size="sm"
      />
    </div>

    <!-- Step 2: 인세 입력 (도서 방식에 따라 인세율 % / 1copy당 정액) -->
    <div v-else class="add-tb__select-section">
      <div class="add-tb__selected">
        <img v-if="selectedTextbook.cover_image_url" :src="selectedTextbook.cover_image_url" :alt="selectedTextbook.title" class="add-tb__cover" />
        <div v-else class="add-tb__cover add-tb__cover-fb">
          <i class="material-icons-round">menu_book</i>
        </div>
        <div class="add-tb__selected-info">
          <p class="add-tb__title">{{ selectedTextbook.title }}</p>
          <p class="add-tb__meta">
            {{ selectedTextbook.barcode }}
            <span v-if="!royaltyTypeLoading" class="add-tb__type-tag">
              {{ isAmountBook ? '정액 (1copy당)' : '인세율 (%)' }}
            </span>
          </p>
        </div>
        <ABtn kind="ghost" size="sm" icon="close" @click="clearSelection">변경</ABtn>
      </div>

      <div v-if="royaltyTypeLoading" class="add-tb__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>인세 방식 확인 중...</p>
      </div>

      <!-- 여유분이 0 이면 무엇을 입력해도 저장이 400 입니다. 값을 넣게 두지 않고 먼저 알려줍니다. -->
      <div v-else-if="isCapExhausted" class="add-tb__blocked">
        <i class="material-icons-round">block</i>
        <div>
          <p class="add-tb__blocked-title">이 교재에는 저자를 더 붙일 수 없습니다</p>
          <p class="add-tb__blocked-body">
            현재 저자 인세 합계가 {{ coAuthorSumLabel }}로 도서 상한을 이미 채웠습니다.
            기존 저자의 인세를 먼저 낮춘 뒤 다시 시도해주세요.
          </p>
        </div>
      </div>

      <div v-else class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">{{ isAmountBook ? 'payments' : 'percent' }}</i>
          {{ isAmountBook ? '1copy당 인세금액' : '인세율' }} <span class="p-field__req">*</span>
        </label>
        <div class="add-tb__rate-row">
          <input
            v-if="isAmountBook"
            v-model.number="royaltyAmount"
            type="number"
            class="p-input"
            min="0"
            :max="maxRoyaltyAmount"
            step="0.01"
            placeholder="0"
          />
          <input
            v-else
            v-model.number="royaltyRate"
            type="number"
            class="p-input"
            min="0"
            :max="maxRoyaltyRate"
            step="0.01"
            placeholder="0.00"
          />
          <span class="add-tb__rate-suffix">{{ isAmountBook ? amountSuffix : '%' }}</span>
        </div>
        <p class="p-field__hint">{{ royaltyHint }}</p>
      </div>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="closeModal">취소</ABtn>
      <ABtn
        kind="yellow"
        icon="add"
        :disabled="!canAddTextbook || addLoading"
        :loading="addLoading"
        @click="addTextbook"
      >{{ addLoading ? '처리중' : '저작물 추가' }}</ABtn>
    </template>
  </AModal>
</template>

<script>
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "AddTextbookModal",
  components: { AModal, ABtn, AEmpty },
  props: {
    show: { type: Boolean, default: false },
    editorIdx: { type: [Number, String], required: true },
  },
  emits: ['close', 'added'],
  data() {
    return {
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      searched: false,
      selectedTextbook: null,
      royaltyRate: 0,
      royaltyAmount: 0,
      royaltyTypeLoading: false,
      addLoading: false,
      searchTimer: null,
    };
  },
  computed: {
    // royalty_type 이 확정되기 전(또는 끝내 못 받은 경우)에는 기존과 같이 인세율 방식으로 봅니다.
    isAmountBook() {
      return this.selectedTextbook?.royalty_type === 'amount';
    },
    // 공저 도서라면 도서 상한이 아니라 남은 여유분이 진짜 상한입니다(가이드 §9.5.2).
    // remaining 은 저자 저작물 목록 응답의 필드라 교재 목록엔 없을 수 있어, 없으면 도서 상한으로 둡니다.
    maxRoyaltyRate() {
      return this.toNumber(this.selectedTextbook?.remaining_royalty_rate)
        ?? this.toNumber(this.selectedTextbook?.max_royalty_rate)
        ?? 100;
    },
    // 상한을 못 받았으면(null) 0 으로 오해하지 않도록 "상한 없음"으로 둡니다.
    maxRoyaltyAmount() {
      return this.toNumber(this.selectedTextbook?.remaining_royalty_amount)
        ?? this.toNumber(this.selectedTextbook?.max_royalty_amount)
        ?? null;
    },
    royaltyCurrency() {
      return this.selectedTextbook?.max_royalty_currency || '';
    },
    amountSuffix() {
      return this.royaltyCurrency ? `${this.royaltyCurrency} / copy` : '/ copy';
    },
    // 상한이 남은 여유분이면 "도서 최대"라고 쓰면 안 됩니다 — 공저에서 값이 달라집니다.
    isRemainingCap() {
      const key = this.isAmountBook ? 'remaining_royalty_amount' : 'remaining_royalty_rate';
      return this.toNumber(this.selectedTextbook?.[key]) !== null;
    },
    // 교재 목록은 "새 저자 추가" 흐름이라 현재 저자 전원의 합계(current_*)가 옵니다.
    // 저작물 목록의 others_*(자기 행 제외)와 기준이 다릅니다(가이드 §9.5.4 경고).
    coAuthorSum() {
      const key = this.isAmountBook ? 'current_royalty_amount_sum' : 'current_royalty_rate_sum';
      return this.toNumber(this.selectedTextbook?.[key]) ?? 0;
    },
    coAuthorSumLabel() {
      if (!this.isAmountBook) return `${this.coAuthorSum}%`;
      return `${this.coAuthorSum}${this.royaltyCurrency ? ` ${this.royaltyCurrency}` : ''} / copy`;
    },
    // 여유분을 실제로 받았고 그 값이 0 이면 입력 자체가 무의미합니다.
    // 상한을 못 받은 경우(null)는 서버 검증에 맡겨야 하므로 여기 해당하지 않습니다.
    isCapExhausted() {
      if (this.royaltyTypeLoading || !this.isRemainingCap) return false;
      const cap = this.isAmountBook ? this.maxRoyaltyAmount : this.maxRoyaltyRate;
      return cap === 0;
    },
    royaltyHint() {
      const capLabel = this.isRemainingCap
        ? (this.coAuthorSum > 0
            ? `기존 저자 ${this.coAuthorSumLabel} 차감 후 남은 한도`
            : '남은 한도')
        : (this.isAmountBook ? '이 도서의 최대 인세금액' : '이 도서의 최대 인세율');
      if (!this.isAmountBook) {
        return `0 ~ ${this.maxRoyaltyRate} 사이의 값을 입력하세요 (소수점 2자리까지, ${capLabel})`;
      }
      if (this.maxRoyaltyAmount === null) {
        return '1copy당 지급할 인세금액을 입력하세요 (상한은 서버에서 검증합니다)';
      }
      const currency = this.royaltyCurrency ? ` ${this.royaltyCurrency}` : '';
      return `0 ~ ${this.maxRoyaltyAmount}${currency} 사이의 1copy당 금액을 입력하세요 (${capLabel})`;
    },
    canAddTextbook() {
      if (!this.selectedTextbook || this.royaltyTypeLoading || this.isCapExhausted) return false;
      const raw = this.isAmountBook ? this.royaltyAmount : this.royaltyRate;
      if (raw === null || raw === undefined || raw === '') return false;
      const value = Number(raw);
      if (!Number.isFinite(value) || value < 0) return false;
      const max = this.isAmountBook ? this.maxRoyaltyAmount : this.maxRoyaltyRate;
      return max === null || value <= max;
    },
  },
  watch: {
    show(newVal) {
      if (!newVal) this.resetModal();
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },

    resetModal() {
      this.searchQuery = '';
      this.searchResults = [];
      this.searched = false;
      this.clearSelection();
    },

    // DECIMAL 이 문자열로 오거나 필드가 아예 없을 때를 "값 없음"(null)으로 모읍니다.
    toNumber(value) {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    },

    clearSelection() {
      this.selectedTextbook = null;
      this.royaltyRate = 0;
      this.royaltyAmount = 0;
      this.royaltyTypeLoading = false;
    },

    debounceSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTextbooks();
      }, 500);
    },

    async searchTextbooks() {
      if (!this.searchQuery.trim()) return;
      this.searchLoading = true;
      this.searched = false;
      try {
        // 공개 검색(/textbook/textbooks)이 아니라 관리자 목록을 씁니다(가이드 §9.5.4).
        // 공개 검색은 비로그인도 부를 수 있어 인세 조건을 싣지 않고, status='approval' 과
        // 콘텐츠 버전 1.0 이상만 반환해 미승인·비매품 교재에는 저자를 붙일 수 없었습니다.
        // search_params 는 필드별 AND 필터라 제목·바코드에 같은 값을 넣으면 0 건이 됩니다.
        // 검색창은 하나이므로 숫자·하이픈만 있으면 바코드로, 그 외에는 교재명으로 보냅니다.
        const term = this.searchQuery.trim();
        const searchParams = /^[0-9-]+$/.test(term) ? { barcode: term } : { title: term };
        const res = await this.$axios({
          method: "get",
          url: "/textbook/editor/textbooks",
          params: {
            limit: 10,
            page: 1,
            search_params: JSON.stringify(searchParams),
          },
          loadingBar: false,
        });
        if (res.status === 200) {
          this.searchResults = res.data?.items || [];
        }
      } catch (error) {
        console.error("교재 검색 실패:", error);
        this.searchResults = [];
      } finally {
        this.searchLoading = false;
        this.searched = true;
      }
    },

    selectTextbookForAdd(book) {
      this.clearSelection();
      this.selectedTextbook = book;
      // 관리자 목록은 인세 방식·상한을 내려주므로 보통 여기서 끝납니다(가이드 §9.5.1).
      // 값이 없을 때만 교재 상세로 한 번 더 확정하고, 그래도 못 알아내면 rate 로 두고
      // 최종 검증은 서버에 맡깁니다(§9.2 각주 — 클라이언트 상한은 1차 안내일 뿐입니다).
      const needsDetail = !book.royalty_type
        || (book.royalty_type === 'amount' && book.max_royalty_amount === undefined);
      if (needsDetail) this.resolveRoyaltyType(book);
    },

    async resolveRoyaltyType(book) {
      if (!book.barcode) return;
      this.royaltyTypeLoading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/editor/textbooks/${encodeURIComponent(book.barcode)}`,
          loadingBar: false,
        });
        // 조회 중 사용자가 다른 교재를 고르면 늦게 온 응답은 버립니다.
        if (res.status === 200 && res.data && this.selectedTextbook?.idx === book.idx) {
          const detail = res.data;
          this.selectedTextbook = {
            ...this.selectedTextbook,
            royalty_type: detail.royalty_type || 'rate',
            max_royalty_rate: detail.max_royalty_rate ?? this.selectedTextbook.max_royalty_rate,
            max_royalty_amount: detail.max_royalty_amount ?? this.selectedTextbook.max_royalty_amount,
            max_royalty_currency: detail.max_royalty_currency || this.selectedTextbook.max_royalty_currency,
          };
        }
      } catch (error) {
        console.error("교재 인세 방식 조회 실패:", error);
      } finally {
        // 소속 검사 없이 끄면, 늦게 온 앞 교재의 응답이 아직 방식 미확정인 뒤 교재의 입력창을 열어
        // amount 도서에 royalty_rate 가 POST 되는 창이 생깁니다(가이드 §9.5.2 — 반대 필드는 400).
        if (this.selectedTextbook?.idx === book.idx) this.royaltyTypeLoading = false;
      }
    },

    async addTextbook() {
      if (!this.canAddTextbook) return;
      this.addLoading = true;
      try {
        // ★ 도서 방식이 요구하는 필드 "하나만" 실어야 합니다(가이드 §9.5.2). 두 필드 모두
        // Optional 이라 서버가 "안 보냄"과 "0 을 보냄"을 구분합니다 — amount 도서에
        // royalty_rate: 0 을 끼워 보내면 400 이므로 키 자체를 만들지 않습니다.
        const data = { textbook_idx: this.selectedTextbook.idx };
        if (this.isAmountBook) data.royalty_amount = Number(this.royaltyAmount);
        else data.royalty_rate = Number(this.royaltyRate);

        await this.$axios({
          method: "post",
          url: `/textbook/editor/work/${this.editorIdx}/textbooks`,
          data,
          loadingBar: true,
        });
        alert("저작물이 추가되었습니다.");
        this.$emit('added');
        this.closeModal();
      } catch (error) {
        console.error("저작물 추가 실패:", error);
        // 400 의 detail 은 그대로 노출할 한국어 문장입니다(§9.5.3). 409 만 서버 문구가
        // 영문이라 한국어로 바꿔 보여줍니다.
        let errorMsg = "저작물 추가에 실패했습니다.";
        if (error.response?.status === 409) errorMsg = "이미 연결된 교재입니다.";
        else errorMsg = this.errorDetail(error, errorMsg);
        alert(errorMsg);
      } finally {
        this.addLoading = false;
      }
    },

    // 422 는 detail 이 배열이라 그대로 alert 하면 [object Object] 가 뜹니다.
    errorDetail(error, fallback) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail) return detail;
      if (Array.isArray(detail)) {
        const msgs = detail.map((d) => d?.msg).filter(Boolean);
        if (msgs.length) return msgs.join('\n');
      }
      return fallback;
    },

    handleImageError(event) {
      event.target.style.display = 'none';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.add-tb {
  &__search-row {
    display: flex;
    gap: 8px;

    > .p-input { flex: 1; }
  }

  &__loading {
    text-align: center;
    padding: 28px;

    p { margin: 12px 0 0; font-size: 12px; color: $p-ink-soft; }
  }

  &__results {
    max-height: 320px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
  }

  &__result {
    text-align: left;
    border: 1px solid $p-line;
    background: $p-panel;
    padding: 10px 12px;
    border-radius: $p-radius-md;
    cursor: pointer;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;
    display: flex;
    align-items: center;
    gap: 12px;

    &:hover {
      border-color: $p-point-deep;
      background: $p-point-soft;
    }
  }

  &__cover-sm {
    width: 40px;
    height: 56px;
    border-radius: $p-radius-sm;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid $p-line;
    object-fit: cover;
  }

  &__cover-fb {
    background: $p-panel-alt;
    display: grid;
    place-items: center;
    color: $p-ink-soft;

    > i { font-size: 20px; }
  }

  &__cover {
    width: 50px;
    height: 70px;
    border-radius: $p-radius-sm;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid $p-line;
    object-fit: cover;
  }

  &__title {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__meta {
    margin: 3px 0 0;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__type-tag {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 4px;
    background: $p-panel;
    border: 1px solid $p-line;
    font-family: $p-font-body;
    font-size: 10px;
    font-weight: 800;
    color: $p-ink-mid;
  }

  &__select-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__selected {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: $p-point-soft;
    border: 1px solid $p-point-deep;
    border-radius: $p-radius-md;
  }

  &__selected-info {
    flex: 1;
    min-width: 0;
  }

  &__rate-row {
    display: flex;
    align-items: center;
    gap: 8px;

    > .p-input { flex: 1; }
  }

  &__rate-suffix {
    font-family: $p-font-mono;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink-mid;
  }

  &__blocked {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    background: $p-err-soft;
    border-left: 3px solid $p-err;
    border-radius: $p-radius-md;

    > i { color: $p-err; font-size: 20px; flex-shrink: 0; }
  }

  &__blocked-title {
    margin: 0;
    font-size: 13px;
    font-weight: 800;
    color: $p-err;
  }

  &__blocked-body {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.55;
    color: $p-ink-mid;
  }
}
</style>
